import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';

/* ── helpers ─────────────────────────────────────────────── */
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000 && d.getDate() === now.getDate()) return d.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
  if (diff < 172800000) return 'Yesterday';
  return d.toLocaleDateString([], { month:'short', day:'numeric' });
};

const fmtMsgTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
};

const fmtMsgDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now); yesterday.setDate(now.getDate()-1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month:'short', day:'numeric', year:'numeric' });
};


/* ── Voice Languages ─────────────────────────────────────── */
const VOICE_LANGS = [
  { code:'en-IN',  label:'🇮🇳 English (India)'   },
  { code:'ml-IN',  label:'🇮🇳 Malayalam'          },
  { code:'hi-IN',  label:'🇮🇳 Hindi'              },
  { code:'ta-IN',  label:'🇮🇳 Tamil'              },
  { code:'te-IN',  label:'🇮🇳 Telugu'             },
  { code:'kn-IN',  label:'🇮🇳 Kannada'            },
  { code:'mr-IN',  label:'🇮🇳 Marathi'            },
  { code:'gu-IN',  label:'🇮🇳 Gujarati'           },
  { code:'pa-IN',  label:'🇮🇳 Punjabi'            },
  { code:'bn-IN',  label:'🇮🇳 Bengali (India)'    },
  { code:'or-IN',  label:'🇮🇳 Odia'               },
  { code:'ur-IN',  label:'🇮🇳 Urdu (India)'       },
  { code:'en-US',  label:'🇺🇸 English (US)'       },
  { code:'en-GB',  label:'🇬🇧 English (UK)'       },
  { code:'en-AU',  label:'🇦🇺 English (Australia)' },
  { code:'ar-SA',  label:'🇸🇦 Arabic'             },
  { code:'zh-CN',  label:'🇨🇳 Chinese (Mandarin)' },
  { code:'fr-FR',  label:'🇫🇷 French'             },
  { code:'de-DE',  label:'🇩🇪 German'             },
  { code:'ja-JP',  label:'🇯🇵 Japanese'           },
  { code:'ko-KR',  label:'🇰🇷 Korean'             },
  { code:'pt-BR',  label:'🇧🇷 Portuguese (Brazil)' },
  { code:'es-ES',  label:'🇪🇸 Spanish'            },
];

const DEFAULT_FAVORITES = ['en-IN','ml-IN','hi-IN','ta-IN','en-US'];

function getVoiceLang()  { return localStorage.getItem('workops_voiceLang') || 'en-IN'; }
function setVoiceLangLS(v) { localStorage.setItem('workops_voiceLang', v); }
function getVoiceFavs()  { try { return JSON.parse(localStorage.getItem('workops_voiceFavs') || JSON.stringify(DEFAULT_FAVORITES)); } catch(e) { return DEFAULT_FAVORITES; } }
function setVoiceFavs(v) { localStorage.setItem('workops_voiceFavs', JSON.stringify(v)); }

/* ── Voice Language Selector ─────────────────────────────── */
function VoiceLangBar({ lang, onLangChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [favs, setFavs] = useState(getVoiceFavs);
  const ref = useRef();

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleFav = (code, e) => {
    e.stopPropagation();
    const newFavs = favs.includes(code) ? favs.filter(f => f !== code) : [...favs, code];
    setFavs(newFavs); setVoiceFavs(newFavs);
  };

  const select = (code) => { onLangChange(code); setVoiceLangLS(code); setOpen(false); setSearch(''); };

  const filtered = VOICE_LANGS.filter(l => l.label.toLowerCase().includes(search.toLowerCase()) || l.code.toLowerCase().includes(search.toLowerCase()));
  const favLangs = VOICE_LANGS.filter(l => favs.includes(l.code));
  const current = VOICE_LANGS.find(l => l.code === lang) || VOICE_LANGS[0];

  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block' }}>
      <button onClick={() => setOpen(v => !v)}
        style={{ display:'flex', alignItems:'center', gap:'4px', padding:'2px 8px', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'11px', fontWeight:600, color:'var(--text1)', cursor:'pointer', whiteSpace:'nowrap' }}>
        {current.label} <span style={{ fontSize:'9px', opacity:0.5 }}>▾</span>
      </button>
      {open && (
        <div style={{ position:'absolute', bottom:'100%', left:0, marginBottom:'4px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'8px', boxShadow:'0 8px 24px rgba(0,0,0,0.2)', zIndex:9999, width:'240px', maxHeight:'320px', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
            <input autoFocus placeholder="Search language..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding:'4px 8px', fontSize:'12px', width:'100%', border:'1px solid var(--border)', borderRadius:'6px', background:'var(--bg3)', color:'var(--text1)', outline:'none', boxSizing:'border-box' }} />
          </div>
          <div style={{ overflowY:'auto', flex:1 }}>
            {!search && favLangs.length > 0 && (
              <div>
                <div style={{ padding:'6px 10px 2px', fontSize:'10px', fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.5px' }}>⭐ Favorites</div>
                {favLangs.map(l => (
                  <div key={l.code} onClick={() => select(l.code)}
                    style={{ display:'flex', alignItems:'center', gap:'6px', padding:'6px 10px', cursor:'pointer', fontSize:'12px', fontWeight:lang === l.code ? 700 : 400, background:lang === l.code ? 'rgba(59,126,240,0.08)' : 'transparent', color:lang === l.code ? 'var(--accent, #3b7ef0)' : 'var(--text1)' }}>
                    <span style={{ flex:1 }}>{l.label}</span>
                    <span onClick={e => toggleFav(l.code, e)} title="Remove from favorites" style={{ color:'#f7c34f', cursor:'pointer', fontSize:'12px' }}>⭐</span>
                    {lang === l.code && <span style={{ color:'var(--accent, #3b7ef0)', fontSize:'11px' }}>✓</span>}
                  </div>
                ))}
                <div style={{ height:'1px', background:'var(--border)', margin:'4px 0' }} />
              </div>
            )}
            {!search && <div style={{ padding:'6px 10px 2px', fontSize:'10px', fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.5px' }}>All Languages</div>}
            {filtered.map(l => (
              <div key={l.code} onClick={() => select(l.code)}
                style={{ display:'flex', alignItems:'center', gap:'6px', padding:'6px 10px', cursor:'pointer', fontSize:'12px', fontWeight:lang === l.code ? 700 : 400, background:lang === l.code ? 'rgba(59,126,240,0.08)' : 'transparent', color:lang === l.code ? 'var(--accent, #3b7ef0)' : 'var(--text1)' }}>
                <span style={{ flex:1 }}>{l.label}</span>
                <span onClick={e => toggleFav(l.code, e)} title={favs.includes(l.code) ? 'Remove from favorites' : 'Add to favorites'}
                  style={{ color:favs.includes(l.code) ? '#f7c34f' : 'var(--text3)', cursor:'pointer', fontSize:'12px', opacity:favs.includes(l.code) ? 1 : 0.4 }}>
                  {favs.includes(l.code) ? '⭐' : '☆'}
                </span>
                {lang === l.code && <span style={{ color:'var(--accent, #3b7ef0)', fontSize:'11px' }}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── VoiceRecorder sub-component ─────────────────────────── */
function VoiceRecorder({ onSend, disabled }) {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (duration > 0) onSend(blob, duration);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= 119) { stopRecording(); return prev; }
          return prev + 1;
        });
      }, 1000);
    } catch (e) {
      alert('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state === 'recording') {
      mediaRecRef.current.stop();
    }
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const cancelRecording = () => {
    chunksRef.current = [];
    setDuration(0);
    if (mediaRecRef.current && mediaRecRef.current.state === 'recording') {
      mediaRecRef.current.onstop = () => {}; // prevent send
      mediaRecRef.current.stop();
    }
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const fmtDur = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  if (!recording) {
    return (
      <button onClick={startRecording} disabled={disabled} title="Record voice message"
        style={{ background:'none', border:'none', cursor: disabled ? 'default' : 'pointer', padding:'6px', color: disabled ? 'var(--text4)' : 'var(--text2)', display:'flex', alignItems:'center' }}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      </button>
    );
  }

  return (
    <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'4px 8px', background:'rgba(239,68,68,0.08)', borderRadius:'8px' }}>
      <span style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#ef4444', animation:'pulse 1s infinite' }}/>
      <span style={{ fontVariantNumeric:'tabular-nums', fontSize:'13px', color:'#ef4444', minWidth:'36px' }}>{fmtDur(duration)}</span>
      <button onClick={cancelRecording} title="Cancel" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding:'4px' }}>✕</button>
      <button onClick={stopRecording} title="Send voice message"
        style={{ background:'#ef4444', border:'none', cursor:'pointer', color:'#fff', borderRadius:'50%', width:'28px', height:'28px', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>
      </button>
    </div>
  );
}

/* ── VoicePlayer sub-component ───────────────────────────── */
function VoicePlayer({ messageId, voiceDuration }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const toggle = async () => {
    if (!audioRef.current) {
      try {
        const res = await api.get(`/chat/voice/${messageId}`, { responseType: 'blob' });
        const blobUrl = URL.createObjectURL(res.data);
        const audio = new Audio(blobUrl);
        audioRef.current = audio;
        audio.onended = () => { setPlaying(false); setProgress(0); clearInterval(intervalRef.current); };
        audio.onerror = () => { setPlaying(false); setProgress(0); clearInterval(intervalRef.current); };
        audio.play();
        setPlaying(true);
        intervalRef.current = setInterval(() => {
          if (audioRef.current) setProgress((audioRef.current.currentTime / (voiceDuration || audioRef.current.duration || 1)) * 100);
        }, 100);
        return;
      } catch (e) { console.error('Failed to load voice', e); return; }
    }
    if (playing) {
      audioRef.current.pause();
      clearInterval(intervalRef.current);
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
      intervalRef.current = setInterval(() => {
        if (audioRef.current) setProgress((audioRef.current.currentTime / (voiceDuration || audioRef.current.duration || 1)) * 100);
      }, 100);
    }
  };

  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } clearInterval(intervalRef.current); }, []);

  const fmtDur = (s) => { s = Math.round(s || 0); return `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`; };

  return (
    <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:'160px' }}>
      <button onClick={toggle} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', padding:'2px', display:'flex' }}>
        {playing ?
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg> :
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
        }
      </button>
      <div style={{ flex:1, height:'4px', background:'rgba(255,255,255,0.25)', borderRadius:'2px', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${progress}%`, background:'currentColor', borderRadius:'2px', transition:'width 0.1s linear' }}/>
      </div>
      <span style={{ fontSize:'11px', opacity:0.8, fontVariantNumeric:'tabular-nums' }}>{fmtDur(voiceDuration)}</span>
    </div>
  );
}

/* ── Main Chat Component ─────────────────────────────────── */
export default function Chat() {
  const { user, can } = useAuth();

  // ── state ───
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chattableUsers, setChattableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [woSearch, setWoSearch] = useState('');
  const [convSearch, setConvSearch] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(0);

  // voice typing
  const [voiceTyping, setVoiceTyping] = useState(false);
  const [voiceLang, setVoiceLang] = useState(getVoiceLang);
  const recognitionRef = useRef(null);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  // ── load work orders ───
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/chat/work-orders');
        const wos = (res.data?.data || res.data || []).filter(w => w.status !== 'Cancelled');
        setWorkOrders(wos);
      } catch (e) { console.error('Failed to load WOs', e); }
      try {
        const res = await api.get('/chat/unread-count');
        setUnreadTotal(res.data?.unread_count || 0);
      } catch (e) {}
      setLoading(false);
    })();
  }, []);

  // ── load conversations when WO selected ───
  const loadConversations = useCallback(async (woId) => {
    try {
      const res = await api.get(`/work-orders/${woId}/chat/conversations`);
      setConversations(res.data?.data || res.data || []);
    } catch (e) {
      console.error('Failed to load conversations', e);
      setConversations([]);
    }
  }, []);

  useEffect(() => {
    if (selectedWO) {
      loadConversations(selectedWO.id);
      setSelectedConv(null);
      setMessages([]);
    }
  }, [selectedWO, loadConversations]);

  // ── load messages when conversation selected ───
  const loadMessages = useCallback(async (convId) => {
    setLoadingMsgs(true);
    try {
      const res = await api.get(`/chat/conversations/${convId}/messages`);
      setMessages(res.data?.data || res.data || []);
    } catch (e) {
      console.error('Failed to load messages', e);
      setMessages([]);
    }
    setLoadingMsgs(false);
  }, []);

  useEffect(() => {
    if (selectedConv) loadMessages(selectedConv.id);
  }, [selectedConv, loadMessages]);

  // ── scroll to bottom on new messages ───
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior:'smooth' });
  }, [messages]);

  // ── poll for new messages ───
  useEffect(() => {
    if (!selectedConv) return;
    const poll = async () => {
      try {
        const lastMsg = messages[messages.length - 1];
        const since = lastMsg ? lastMsg.created_at : new Date(0).toISOString();
        const res = await api.get(`/chat/conversations/${selectedConv.id}/poll`, { params: { since } });
        const newMsgs = res.data?.data || res.data || [];
        if (newMsgs.length > 0) {
          setMessages(prev => {
            const ids = new Set(prev.map(m => m.id));
            return [...prev, ...newMsgs.filter(m => !ids.has(m.id))];
          });
        }
      } catch (e) {}
      // also refresh conversation list for unread counts
      if (selectedWO) {
        try {
          const res = await api.get(`/work-orders/${selectedWO.id}/chat/conversations`);
          setConversations(res.data?.data || res.data || []);
        } catch (e) {}
      }
    };
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [selectedConv, messages, selectedWO]);

  // ── send text message ───
  const sendMessage = async () => {
    if (!msgText.trim() || !selectedConv || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/chat/conversations/${selectedConv.id}/messages`, { body: msgText.trim() });
      const newMsg = res.data?.data || res.data;
      setMessages(prev => [...prev, newMsg]);
      setMsgText('');
      inputRef.current?.focus();
    } catch (e) {
      alert('Failed to send: ' + (e.response?.data?.message || e.message));
    }
    setSending(false);
  };

  // ── send voice message ───
  const sendVoiceMessage = async (blob, duration) => {
    if (!selectedConv) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'voice.webm');
      fd.append('duration', String(Math.round(duration)));
      const res = await api.post(`/chat/conversations/${selectedConv.id}/voice-messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newMsg = res.data?.data || res.data;
      setMessages(prev => [...prev, newMsg]);
    } catch (e) {
      alert('Failed to send voice: ' + (e.response?.data?.message || e.message));
    }
    setSending(false);
  };

  // ── start new conversation ───
  const startConversation = async (targetUserId) => {
    if (!selectedWO) return;
    try {
      const res = await api.post(`/work-orders/${selectedWO.id}/chat/conversations`, { user_id: targetUserId });
      const conv = res.data?.data || res.data;
      await loadConversations(selectedWO.id);
      setSelectedConv(conv);
      setShowNewChat(false);
    } catch (e) {
      alert('Failed to start: ' + (e.response?.data?.message || e.message));
    }
  };

  // ── load chattable users ───
  const loadChattableUsers = useCallback(async (woId) => {
    try {
      const res = await api.get(`/work-orders/${woId}/chat/users`);
      setChattableUsers(res.data?.data || res.data || []);
    } catch (e) {
      setChattableUsers([]);
    }
  }, []);

  // ── voice typing ───
  const toggleVoiceTyping = () => {
    if (voiceTyping) {
      recognitionRef.current?.stop();
      setVoiceTyping(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Speech recognition is not supported in this browser.'); return; }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = voiceLang || 'en-IN';
    recognition.onresult = (e) => {
      let transcript = '';
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setMsgText(transcript);
    };
    recognition.onerror = () => setVoiceTyping(false);
    recognition.onend = () => setVoiceTyping(false);
    recognition.start();
    recognitionRef.current = recognition;
    setVoiceTyping(true);
  };

  // ── filter WOs ───
  const filteredWOs = workOrders.filter(wo => {
    if (!woSearch) return true;
    const s = woSearch.toLowerCase();
    return (wo.title || '').toLowerCase().includes(s) ||
           (wo.client?.name || wo.client_name || '').toLowerCase().includes(s) ||
           String(wo.id).includes(s);
  });

  // ── filtered conversations ───
  const filteredConvs = conversations.filter(conv => {
    if (!convSearch) return true;
    const otherName = getOtherParticipantName(conv);
    return otherName.toLowerCase().includes(convSearch.toLowerCase());
  });

  // ── get other participant name ───
  function getOtherParticipantName(conv) {
    if (!conv) return '';
    const other = conv.other_user || (conv.participant_one_user?.id === user?.id ? conv.participant_two_user : conv.participant_one_user);
    return other?.name || 'Unknown';
  }

  function getOtherParticipantRole(conv) {
    if (!conv) return '';
    const other = conv.other_user || (conv.participant_one_user?.id === user?.id ? conv.participant_two_user : conv.participant_one_user);
    return other?.role || '';
  }

  const clientName = (wo) => wo.client?.name || wo.client_name || '';

  if (loading) return <div style={{ padding:'40px', textAlign:'center', color:'var(--text3)' }}>Loading…</div>;

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div style={{ display:'flex', height:'calc(100vh - 64px)', background:'var(--bg1)', overflow:'hidden' }}>
      {/* ── WO Sidebar ─────────────────────────────────── */}
      <div style={{ width:'240px', minWidth:'240px', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', background:'var(--bg2)' }}>
        <div style={{ padding:'12px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text1)', marginBottom:'8px' }}>Work Orders</div>
          <input
            placeholder="Search WOs…"
            value={woSearch}
            onChange={e => setWoSearch(e.target.value)}
            style={{ width:'100%', padding:'6px 10px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'6px', background:'var(--bg3)', color:'var(--text1)', outline:'none', boxSizing:'border-box' }}
          />
        </div>
        <div style={{ flex:1, overflowY:'auto' }}>
          {filteredWOs.length === 0 && <div style={{ padding:'20px', textAlign:'center', fontSize:'12px', color:'var(--text4)' }}>No work orders</div>}
          {filteredWOs.map(wo => (
            <div key={wo.id} onClick={() => setSelectedWO(wo)}
              style={{
                padding:'10px 12px', cursor:'pointer', borderBottom:'1px solid var(--border)',
                background: selectedWO?.id === wo.id ? 'var(--bg3)' : 'transparent',
                borderLeft: selectedWO?.id === wo.id ? '3px solid var(--accent, #3b7ef0)' : '3px solid transparent',
              }}>
              <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text1)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', flex:1 }}>{wo.title || `WO #${wo.id}`}</div>
                {wo.unread_count > 0 && <span style={{ background:'#ef4444', color:'#fff', fontSize:'10px', fontWeight:700, borderRadius:'10px', padding:'1px 6px', minWidth:'18px', textAlign:'center', flexShrink:0 }}>{wo.unread_count}</span>}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                <div style={{ fontSize:'11px', color:'var(--text3)', marginTop:'2px', flex:1 }}>{clientName(wo)}{wo.wo_number ? ` · ${wo.wo_number}` : ''}</div>
                {wo.has_chats && !wo.unread_count && <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:'var(--accent, #3b7ef0)', flexShrink:0 }}/>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Conversation List ──────────────────────────── */}
      {selectedWO ? (
        <div style={{ width:'260px', minWidth:'260px', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', background:'var(--bg1)' }}>
          <div style={{ padding:'12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'8px' }}>
            <input
              placeholder="Search chats…"
              value={convSearch}
              onChange={e => setConvSearch(e.target.value)}
              style={{ flex:1, padding:'6px 10px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'6px', background:'var(--bg3)', color:'var(--text1)', outline:'none' }}
            />
            <button onClick={() => { setShowNewChat(true); loadChattableUsers(selectedWO.id); }}
              title="New chat"
              style={{ background:'var(--accent, #3b7ef0)', border:'none', borderRadius:'6px', color:'#fff', width:'28px', height:'28px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
          </div>
          <div style={{ flex:1, overflowY:'auto' }}>
            {filteredConvs.length === 0 && !showNewChat && (
              <div style={{ padding:'30px 16px', textAlign:'center', color:'var(--text4)', fontSize:'12px' }}>
                {conversations.length === 0 ? 'No conversations yet' : 'No matches'}
              </div>
            )}
            {filteredConvs.map(conv => {
              const otherName = getOtherParticipantName(conv);
              const otherRole = getOtherParticipantRole(conv);
              const isClient = otherRole === 'client';
              const unread = conv.unread_count || 0;
              const lastMsg = conv.last_message;
              return (
                <div key={conv.id} onClick={() => { setSelectedConv(conv); setShowNewChat(false); }}
                  style={{
                    padding:'10px 12px', cursor:'pointer', borderBottom:'1px solid var(--border)',
                    background: selectedConv?.id === conv.id ? 'var(--bg3)' : 'transparent',
                  }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', minWidth:0 }}>
                      <div style={{
                        width:'32px', height:'32px', borderRadius:'50%', flexShrink:0,
                        background: isClient ? '#e0f2fe' : '#ede9fe',
                        color: isClient ? '#0284c7' : '#7c3aed',
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:700,
                      }}>{otherName.charAt(0).toUpperCase()}</div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:'13px', fontWeight: unread > 0 ? 700 : 500, color:'var(--text1)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {otherName}
                        </div>
                        {isClient && <span style={{ fontSize:'10px', color:'#0284c7', background:'#e0f2fe', padding:'1px 5px', borderRadius:'3px' }}>Client</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'4px', flexShrink:0 }}>
                      <span style={{ fontSize:'10px', color:'var(--text4)' }}>{fmtTime(conv.last_message_at)}</span>
                      {unread > 0 && (
                        <span style={{ background:'var(--accent, #3b7ef0)', color:'#fff', fontSize:'10px', fontWeight:700, borderRadius:'10px', padding:'1px 6px', minWidth:'16px', textAlign:'center' }}>{unread}</span>
                      )}
                    </div>
                  </div>
                  {lastMsg && (
                    <div style={{ fontSize:'11px', color:'var(--text3)', marginTop:'4px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', paddingLeft:'38px' }}>
                      {lastMsg.message_type === 'voice' ? '🎤 Voice message' : (lastMsg.body || '').substring(0, 60)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)', fontSize:'14px' }}>
          <div style={{ textAlign:'center' }}>
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="var(--text4)" strokeWidth="1.2" style={{ marginBottom:'12px' }}>
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            <div>Select a work order to start chatting</div>
          </div>
        </div>
      )}

      {/* ── Message Thread / New Chat ──────────────────── */}
      {selectedWO && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
          {showNewChat ? (
            /* New chat user picker */
            <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
              <div style={{ padding:'16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ fontWeight:600, fontSize:'14px', color:'var(--text1)' }}>Start a new chat</div>
                <button onClick={() => setShowNewChat(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:'18px' }}>✕</button>
              </div>
              <div style={{ flex:1, overflowY:'auto', padding:'8px' }}>
                {chattableUsers.length === 0 && <div style={{ padding:'20px', textAlign:'center', color:'var(--text4)', fontSize:'13px' }}>No users available to chat</div>}
                {chattableUsers.filter(u => u.id !== user?.id).map(u => (
                  <div key={u.id} onClick={() => startConversation(u.id)}
                    style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', cursor:'pointer', borderRadius:'8px', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <div style={{
                      width:'36px', height:'36px', borderRadius:'50%',
                      background: u.role === 'client' ? '#e0f2fe' : '#ede9fe',
                      color: u.role === 'client' ? '#0284c7' : '#7c3aed',
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:700, flexShrink:0,
                    }}>{(u.name || '?').charAt(0).toUpperCase()}</div>
                    <div>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text1)' }}>{u.name}</div>
                      <div style={{ fontSize:'11px', color:'var(--text3)' }}>
                        {u.role === 'client' ? '👔 Client' : (u.role_name || u.role || 'Team member')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : selectedConv ? (
            /* Message thread */
            <>
              {/* Header */}
              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'10px', background:'var(--bg2)' }}>
                <div style={{
                  width:'36px', height:'36px', borderRadius:'50%',
                  background: getOtherParticipantRole(selectedConv) === 'client' ? '#e0f2fe' : '#ede9fe',
                  color: getOtherParticipantRole(selectedConv) === 'client' ? '#0284c7' : '#7c3aed',
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:700, flexShrink:0,
                }}>{getOtherParticipantName(selectedConv).charAt(0).toUpperCase()}</div>
                <div>
                  <div style={{ fontSize:'14px', fontWeight:600, color:'var(--text1)' }}>{getOtherParticipantName(selectedConv)}</div>
                  <div style={{ fontSize:'11px', color:'var(--text3)' }}>
                    {getOtherParticipantRole(selectedConv) === 'client' ? '👔 Client' : 'Team member'}
                    {' · '}{selectedWO.title || `WO #${selectedWO.id}`}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex:1, overflowY:'auto', padding:'16px', display:'flex', flexDirection:'column', gap:'2px' }}>
                {loadingMsgs && <div style={{ textAlign:'center', color:'var(--text4)', padding:'20px' }}>Loading…</div>}
                {!loadingMsgs && messages.length === 0 && (
                  <div style={{ textAlign:'center', color:'var(--text4)', padding:'40px', fontSize:'13px' }}>No messages yet. Say hello!</div>
                )}
                {messages.map((msg, idx) => {
                  const isMine = msg.sender_id === user?.id;
                  const prevMsg = messages[idx - 1];
                  const showDate = !prevMsg || fmtMsgDate(msg.created_at) !== fmtMsgDate(prevMsg.created_at);

                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && (
                        <div style={{ textAlign:'center', margin:'12px 0 8px', fontSize:'11px', color:'var(--text4)' }}>
                          <span style={{ background:'var(--bg2)', padding:'2px 10px', borderRadius:'10px' }}>{fmtMsgDate(msg.created_at)}</span>
                        </div>
                      )}
                      <div style={{ display:'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom:'2px' }}>
                        <div style={{
                          maxWidth:'65%', padding: msg.message_type === 'voice' ? '8px 12px' : '8px 14px',
                          borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          background: isMine ? 'var(--accent, #3b7ef0)' : 'var(--bg3)',
                          color: isMine ? '#fff' : 'var(--text1)',
                          fontSize:'13px', lineHeight:'1.45', wordBreak:'break-word',
                        }}>
                          {!isMine && (
                            <div style={{ fontSize:'11px', fontWeight:600, marginBottom:'2px', opacity:0.7 }}>
                              {msg.sender?.name || 'Unknown'}
                            </div>
                          )}
                          {msg.message_type === 'voice' ? (
                            <VoicePlayer messageId={msg.id} voiceDuration={msg.voice_duration} />
                          ) : (
                            <div>{msg.body}</div>
                          )}
                          <div style={{ fontSize:'10px', opacity:0.6, marginTop:'4px', textAlign:'right' }}>
                            {fmtMsgTime(msg.created_at)}
                            {isMine && msg.read_at && <span style={{ marginLeft:'4px' }}>✓✓</span>}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Language selector + Input bar */}
              <div style={{ borderTop:'1px solid var(--border)', background:'var(--bg2)' }}>
                <div style={{ padding:'6px 16px 0', display:'flex', alignItems:'center' }}>
                  <VoiceLangBar lang={voiceLang} onLangChange={setVoiceLang} />
                </div>
                <div style={{ padding:'6px 16px 10px', display:'flex', alignItems:'flex-end', gap:'8px' }}>
                {/* Voice typing button */}
                <button onClick={toggleVoiceTyping} title={voiceTyping ? 'Stop voice typing' : 'Voice typing'}
                  style={{ background:'none', border:'none', cursor:'pointer', padding:'6px', color: voiceTyping ? '#ef4444' : 'var(--text2)', display:'flex', alignItems:'center', flexShrink:0 }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
                    {voiceTyping && <line x1="1" y1="1" x2="23" y2="23" stroke="#ef4444" strokeWidth="2.5"/>}
                    <path d="M19 10v2a7 7 0 01-14 0v-2"/>
                  </svg>
                  {voiceTyping && <span style={{ fontSize:'10px', color:'#ef4444', marginLeft:'2px' }}>ON</span>}
                </button>

                <textarea
                  ref={inputRef}
                  placeholder="Type a message…"
                  value={msgText}
                  onChange={e => setMsgText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  rows={1}
                  style={{
                    flex:1, padding:'8px 12px', fontSize:'13px', border:'1px solid var(--border)', borderRadius:'10px',
                    background:'var(--bg3)', color:'var(--text1)', outline:'none', resize:'none', fontFamily:'inherit',
                    maxHeight:'100px', lineHeight:'1.4',
                  }}
                  onInput={e => { e.target.style.height='auto'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }}
                />

                <VoiceRecorder onSend={sendVoiceMessage} disabled={sending} />

                <button onClick={sendMessage} disabled={!msgText.trim() || sending} title="Send"
                  style={{
                    background: msgText.trim() ? 'var(--accent, #3b7ef0)' : 'var(--bg3)',
                    border:'none', borderRadius:'50%', width:'34px', height:'34px', cursor: msgText.trim() ? 'pointer' : 'default',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                    color: msgText.trim() ? '#fff' : 'var(--text4)',
                    transition:'all 0.15s',
                  }}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>
                </button>
                </div>
              </div>
            </>
          ) : (
            /* No conversation selected */
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)', fontSize:'13px' }}>
              <div style={{ textAlign:'center' }}>
                <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="var(--text4)" strokeWidth="1.2" style={{ marginBottom:'10px' }}>
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
                </svg>
                <div>Select a conversation or start a new chat</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── pulse animation ─────────────────────────────── */}
      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
      `}</style>
    </div>
  );
}
